import type { SearchResultJobV5 } from '@seek/chalice-types';
import { Box, Loader } from 'braid-design-system';
import {
  type ComponentProps,
  memo,
  type ReactNode,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

import { useSearchPath } from 'src/components/Search/SearchBar/SearchBar';
import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import useAuthenticated from 'src/hooks/useAuthenticated';
import { useBehaviouralCuesFiltersFeatureFlag } from 'src/store/featureFlags/hooks';
import { selectIsHomePage, selectTags } from 'src/store/location/selectors';
import { useDispatch, useSelector } from 'src/store/react';
import type { SearchViewModelCompanySuggestion } from 'src/store/results/types';
import { useRunSearch } from 'src/store/search/useRunSearch';
import {
  selectIsResultsLoading,
  selectLocationPageNumber,
  selectPaginationTotalPages,
} from 'src/store/selectors';

import type SearchResultList from '../SearchResultList/SearchResultList';
import ZeroResults from '../ZeroResults/ZeroResults';

import BehaviouralCuesFiltersMessage from './BehaviouralCuesFiltersMessage';
import JobDetailView from './JobDetailView';
import ListView from './ListView';
import SplitViewLayout from './SplitViewLayout/SplitViewLayout';
import { useShowResultSummaryWithBehaviouralCuesFiltersEnabled } from './useShowResultSummaryWithBehaviouralCuesFiltersEnabled/useShowResultSummaryWithBehaviouralCuesFiltersEnabled';
import useSplitViewOpen1stJobAdEffect from './useSplitViewOpen1stJobAdEffect/useSplitViewOpen1stJobAdEffect';

import * as styles from './SplitView.css';

export interface SplitViewProps {
  jobs: SearchResultJobV5[];
  resultSummary: ReactNode;
  isCompanySearch: ComponentProps<typeof SearchResultList>['isCompanySearch'];
  companySuggestion?: SearchViewModelCompanySuggestion;
  isZeroResults: boolean;
}

const SrpLoader = () => (
  <Box className={styles.LoadingWrapItem} zIndex={1} pointerEvents="none">
    <Box width="full" className={styles.content}>
      <Box
        paddingY="xlarge"
        style={{ height: 400 }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-automation="search-results-loader"
      >
        <Loader size="large" />
      </Box>
    </Box>
  </Box>
);

const SplitView = ({
  jobs,
  resultSummary,
  isCompanySearch,
  companySuggestion,
  isZeroResults = false,
}: SplitViewProps) => {
  const {
    selectedJob,
    setSelectedJob,
    currentJobDetails,
    fetchJobDetails,
    isLoading,
  } = useSplitViewContext();

  const isSearchLoading = useSelector(selectIsResultsLoading);
  const totalPages = useSelector(selectPaginationTotalPages);
  const currentPageNumber = useSelector(selectLocationPageNumber);
  const searchTags = useSelector(selectTags);
  const isBehaviouralCuesFiltersEnabled =
    useBehaviouralCuesFiltersFeatureFlag();
  const authenticateState = useAuthenticated();
  const isNewToYou = searchTags === 'new';

  useSplitViewOpen1stJobAdEffect(jobs);

  // SSA we need to check for count, if it's null it means we haven't done
  // a search on the server side - probably because we are searching for
  // a 'new' tag. This is to prevent first search results under 'new to you'
  // to appear before client side auth steps in and removes all the results
  // that the user had already seen.
  const newToYouCount = useSelector((c) => c.results.totalCountNewToYou) ?? -1;
  const dispatch = useDispatch();
  const runSearch = useRunSearch();
  const { currentSearchPath } = useSearchPath();
  useEffect(() => {
    if (
      !isLoading &&
      isNewToYou &&
      (newToYouCount === null || newToYouCount < 0)
    ) {
      dispatch(
        runSearch({
          pathname: currentSearchPath,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newToYouCount, isLoading, currentSearchPath, dispatch]);

  const isHomePage = useSelector(selectIsHomePage);
  const hideResults = isSearchLoading || isZeroResults;

  const showResultSummaryWithBehaviouralCuesFiltersEnabled =
    useShowResultSummaryWithBehaviouralCuesFiltersEnabled();

  // Memoize the job selection callback to prevent recreation on each render
  const handleSetSelectedJob = useCallback(
    (job: SearchResultJobV5 | undefined) => {
      setSelectedJob(job);
    },
    [setSelectedJob],
  );

  // Memoize message props
  const messageProps = useMemo(
    () => ({
      authenticateState,
      isNewToYou,
      isZeroResults,
      companySuggestion,
    }),
    [authenticateState, isNewToYou, isZeroResults, companySuggestion],
  );

  // Memoize the ListView component instance
  const memoizedListView = useMemo(
    () => (
      <ListView
        {...{
          resultSummary,
          hideResults,
          jobs,
          isCompanySearch,
          setCurrentJob: handleSetSelectedJob,
          selectedJobId: selectedJob?.id,
          currentPageNumber,
          totalPages,
          isNewToYou,
          showSummary: showResultSummaryWithBehaviouralCuesFiltersEnabled,
        }}
      />
    ),
    [
      resultSummary,
      hideResults,
      jobs,
      isCompanySearch,
      handleSetSelectedJob,
      selectedJob?.id,
      currentPageNumber,
      totalPages,
      isNewToYou,
      showResultSummaryWithBehaviouralCuesFiltersEnabled,
    ],
  );

  // Memoize the JobDetailView component instance
  const memoizedDetailView = useMemo(
    () => (
      <JobDetailView
        {...{
          hideResults,
          selectedJob,
          currentJobDetails,
          fetchJobDetails,
          isLoading,
        }}
      />
    ),
    [hideResults, selectedJob, currentJobDetails, fetchJobDetails, isLoading],
  );

  return (
    <Box data-automation="split-view" className={styles.LoadingWrap}>
      {isSearchLoading && <SrpLoader />}

      {isBehaviouralCuesFiltersEnabled && (
        <>
          {!isSearchLoading && (
            <BehaviouralCuesFiltersMessage {...messageProps} />
          )}

          <Box className={styles.LoadingWrapItem}>
            <SplitViewLayout
              ListView={memoizedListView}
              DetailView={memoizedDetailView}
            />
          </Box>
        </>
      )}

      {!isBehaviouralCuesFiltersEnabled && (
        <>
          {!isSearchLoading && isZeroResults && (
            <Box
              className={styles.LoadingWrapItem}
              zIndex={1}
              marginTop="xxxlarge"
            >
              <Box width="full" className={styles.content}>
                {!isHomePage && (
                  <ZeroResults companySuggestion={companySuggestion} />
                )}
              </Box>
            </Box>
          )}

          {!isSearchLoading && !isZeroResults && (
            <Box className={styles.LoadingWrapItem}>
              <SplitViewLayout
                ListView={memoizedListView}
                DetailView={memoizedDetailView}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default memo(SplitView);
